import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Drawer,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { AppIconButton, AppListPage, AppTextField } from '../../components/design-system';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import { providerTenantsService, type Tenant } from '../../services/ProviderTenantsService';
import { opsUnattestedService } from '../../services/OpsUnattestedService';
import type {
  UnattestedAttempt,
  UnattestedAttemptStatus,
} from '@cnapse/shared/schemas.js';
import { EnhancedTable, type TableColumn, type TableAction, type FilterConfig } from '../../components/ui/EnhancedTable';

const STATUS_COLORS: Record<UnattestedAttemptStatus, 'error' | 'warning' | 'success'> = {
  open: 'error',
  acknowledged: 'warning',
  resolved: 'success',
};

type DateRangePreset = '24h' | '7d' | '30d' | '90d' | 'custom';

function formatDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function presetStartIso(preset: DateRangePreset): string {
  const milliseconds: Record<DateRangePreset, number> = {
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
    '90d': 90 * 24 * 60 * 60 * 1000,
    custom: 0,
  };

  return new Date(Date.now() - milliseconds[preset]).toISOString();
}

function startOfDayIso(dateValue: string): string {
  return new Date(`${dateValue}T00:00:00.000`).toISOString();
}

function endOfDayIso(dateValue: string): string {
  return new Date(`${dateValue}T23:59:59.999`).toISOString();
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box display="flex" py={0.75} borderBottom="1px solid" borderColor="divider">
      <Typography variant="body2" color="text.secondary" sx={{ width: 200, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" component="div">{value ?? '—'}</Typography>
    </Box>
  );
}

export default function UnattestedDashboard() {
  const [rows, setRows] = useState<UnattestedAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedAttempt, setSelectedAttempt] = useState<UnattestedAttempt | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('7d');
  const [customFromDate, setCustomFromDate] = useState(() => formatDateInputValue(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)));
  const [customToDate, setCustomToDate] = useState(() => formatDateInputValue(new Date()));
  const [tableFilters, setTableFilters] = useState<Record<string, string>>({
    tenantId: '',
    siteId: '',
    deviceType: '',
    status: '',
  });
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let cursor: string | undefined;
      const allRows: UnattestedAttempt[] = [];

      do {
        const result = await opsUnattestedService.getUnattestedAttemptList({
          cursor,
          limit: 200,
        });
        allRows.push(...result.data.attempts);
        cursor = result.data.nextCursor ?? undefined;
      } while (cursor);

      const tenantsData = await providerTenantsService.getTenants();

      setRows(allRows);
      setTenants(tenantsData as Tenant[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load unattested attempts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, tableFilters, dateRangePreset, customFromDate, customToDate, pageSize]);

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void loadData()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [loadData, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  const filterFrom = useMemo(() => {
    if (dateRangePreset === 'custom') {
      return customFromDate ? startOfDayIso(customFromDate) : null;
    }
    return presetStartIso(dateRangePreset);
  }, [customFromDate, dateRangePreset]);

  const filterTo = useMemo(() => {
    if (dateRangePreset !== 'custom') {
      return null;
    }
    return customToDate ? endOfDayIso(customToDate) : null;
  }, [customToDate, dateRangePreset]);

  const tenantNameById = useMemo(() => {
    return new Map<string, string>(tenants.map((tenant) => [String(tenant.id), tenant.name]));
  }, [tenants]);

  const filterOptions = useMemo((): FilterConfig[] => {
    const siteOptions = Array.from(new Set(rows.map((row) => row.siteId).filter(Boolean) as string[]))
      .sort((left, right) => left.localeCompare(right))
      .map((siteId) => ({ label: siteId, value: siteId }));
    const deviceTypes = Array.from(new Set(rows.map((row) => row.deviceType).filter(Boolean) as string[]))
      .sort((left, right) => left.localeCompare(right))
      .map((deviceType) => ({ label: deviceType, value: deviceType }));

    return [
      {
        field: 'tenantId',
        label: 'Tenant',
        type: 'select',
        options: tenants.map((tenant) => ({ label: tenant.name, value: tenant.id })),
      },
      {
        field: 'siteId',
        label: 'Site',
        type: 'select',
        options: siteOptions,
      },
      {
        field: 'deviceType',
        label: 'Device Type',
        type: 'select',
        options: deviceTypes,
      },
      {
        field: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Open', value: 'open' },
          { label: 'Acknowledged', value: 'acknowledged' },
          { label: 'Resolved', value: 'resolved' },
        ],
      },
    ];
  }, [rows, tenants]);

  const filteredRows = useMemo(() => {
    const searchValue = searchTerm.trim().toLowerCase();

    return rows.filter((row) => {
      if (tableFilters.tenantId && row.tenantId !== tableFilters.tenantId) return false;
      if (tableFilters.siteId && row.siteId !== tableFilters.siteId) return false;
      if (tableFilters.deviceType && row.deviceType !== tableFilters.deviceType) return false;
      if (tableFilters.status && row.status !== tableFilters.status) return false;
      if (filterFrom && row.lastSeenAt < filterFrom) return false;
      if (filterTo && row.lastSeenAt > filterTo) return false;

      if (!searchValue) return true;

      const tenantName = tenantNameById.get(row.tenantId) ?? '';
      const identifiers = row.observedIdentifiers
        ? Object.values(row.observedIdentifiers).filter(Boolean).join(' ')
        : '';
      const haystack = [
        row.attemptId,
        row.tenantId,
        tenantName,
        row.siteId ?? '',
        row.deviceType ?? '',
        row.status,
        identifiers,
        row.sourceGatewaySerialNumber ?? '',
        row.reasonCode ?? '',
        row.reasonDetail ?? '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(searchValue);
    });
  }, [rows, searchTerm, tableFilters, filterFrom, filterTo, tenantNameById]);

  const pagedRows = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredRows.slice(startIndex, startIndex + pageSize);
  }, [filteredRows, page, pageSize]);

  const columns: TableColumn<UnattestedAttempt>[] = [
    {
      field: 'lastSeenAt',
      headerName: 'Last Seen (UTC)',
      renderCell: (row: UnattestedAttempt) => new Date(row.lastSeenAt).toLocaleString(),
    },
    {
      field: 'firstSeenAt',
      headerName: 'First Seen (UTC)',
      renderCell: (row: UnattestedAttempt) => new Date(row.firstSeenAt).toLocaleString(),
    },
    {
      field: 'tenantId',
      headerName: 'Tenant',
      renderCell: (row: UnattestedAttempt) => {
        const tenant = tenants.find((t) => t.id === row.tenantId);
        return tenant?.name ?? row.tenantId;
      },
    },
    { field: 'siteId', headerName: 'Site', renderCell: (row: UnattestedAttempt) => row.siteId ?? '—' },
    { field: 'deviceType', headerName: 'Device Type', renderCell: (row: UnattestedAttempt) => row.deviceType ?? '—' },
    {
      field: 'observedIdentifiers',
      headerName: 'Identifiers',
      renderCell: (row: UnattestedAttempt) => {
        if (!row.observedIdentifiers) return '—';
        const parts: string[] = [];
        if (row.observedIdentifiers.serialNumber) parts.push(`serial=${row.observedIdentifiers.serialNumber}`);
        if (row.observedIdentifiers.macAddress) parts.push(`mac=${row.observedIdentifiers.macAddress}`);
        if (row.observedIdentifiers.deviceId) parts.push(`deviceId=${row.observedIdentifiers.deviceId}`);
        return <Typography variant="body2" fontFamily="monospace">{parts.join(', ') || '—'}</Typography>;
      },
    },
    { field: 'attemptCount', headerName: 'Attempts', renderCell: (row: UnattestedAttempt) => row.attemptCount },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (row: UnattestedAttempt) => (
        <Chip label={row.status} color={STATUS_COLORS[row.status]} size="small" />
      ),
    },
  ];

  const actions: TableAction<UnattestedAttempt>[] = [
    {
      label: 'View Details',
      icon: <InfoIcon />,
      onClick: (row: UnattestedAttempt) => setSelectedAttempt(row),
    },
  ];

  return (
    <AppListPage>
      {error && (
        <Alert severity="error" sx={{ flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <EnhancedTable
        data={pagedRows}
        totalCount={filteredRows.length}
        columns={columns}
        actions={actions}
        getRowId={(row) => row.attemptId}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
        searchable={true}
        searchTerm={searchTerm}
        searchPlaceholder="Search identifier, gateway, reason, tenant..."
        onSearchChange={setSearchTerm}
        filters={tableFilters}
        filterOptions={filterOptions}
        onFilterChange={(filters) => setTableFilters(filters as Record<string, string>)}
        toolbarLeftContent={(
          <Box sx={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Window:
            </Typography>
            <ToggleButtonGroup
              value={dateRangePreset}
              exclusive
              size="small"
              onChange={(_, value: DateRangePreset | null) => {
                if (value) {
                  setDateRangePreset(value);
                }
              }}
            >
              {(['24h', '7d', '30d', '90d', 'custom'] as DateRangePreset[]).map((preset) => (
                <ToggleButton key={preset} value={preset}>{preset}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            {dateRangePreset === 'custom' && (
              <>
                <AppTextField
                  label="From"
                  type="date"
                  size="small"
                  value={customFromDate}
                  onChange={(event) => setCustomFromDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />
                <AppTextField
                  label="To"
                  type="date"
                  size="small"
                  value={customToDate}
                  onChange={(event) => setCustomToDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: customFromDate || undefined }}
                  sx={{ minWidth: 160 }}
                />
              </>
            )}
          </Box>
        )}
        emptyStateTitle="No unattested attempts found"
        emptyStateDescription="Use search or filters, or refresh to try again."
        maxHeight="100%"
      />

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(selectedAttempt)}
        onClose={() => setSelectedAttempt(null)}
        PaperProps={{ sx: { width: 480, p: 3 } }}
      >
        {selectedAttempt && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Attempt Details</Typography>
              <AppIconButton onClick={() => setSelectedAttempt(null)} aria-label="Close details"><CloseIcon /></AppIconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <DetailRow label="Attempt ID" value={<Typography fontFamily="monospace" fontSize={12}>{selectedAttempt.attemptId}</Typography>} />
            <DetailRow label="Tenant" value={tenants.find((t) => t.id === selectedAttempt.tenantId)?.name ?? selectedAttempt.tenantId} />
            <DetailRow label="Site" value={selectedAttempt.siteId} />
            <DetailRow label="Device Type" value={selectedAttempt.deviceType} />
            <DetailRow label="Status" value={<Chip label={selectedAttempt.status} color={STATUS_COLORS[selectedAttempt.status]} size="small" />} />

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Observed Identifiers</Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
              <DetailRow label="Serial Number" value={selectedAttempt.observedIdentifiers?.serialNumber} />
              <DetailRow label="MAC Address" value={selectedAttempt.observedIdentifiers?.macAddress} />
              <DetailRow label="Device ID" value={selectedAttempt.observedIdentifiers?.deviceId} />
            </Paper>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Source</Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
              <DetailRow label="Gateway Serial" value={selectedAttempt.sourceGatewaySerialNumber} />
              <DetailRow label="Channel" value={selectedAttempt.sourceChannel} />
            </Paper>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Reason</Typography>
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
              <DetailRow label="Reason Code" value={selectedAttempt.reasonCode} />
              <DetailRow label="Reason Detail" value={selectedAttempt.reasonDetail} />
            </Paper>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Metrics</Typography>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <DetailRow label="First Seen" value={new Date(selectedAttempt.firstSeenAt).toLocaleString()} />
              <DetailRow label="Last Seen" value={new Date(selectedAttempt.lastSeenAt).toLocaleString()} />
              <DetailRow label="Attempt Count" value={selectedAttempt.attemptCount} />
            </Paper>
          </Box>
        )}
      </Drawer>
    </AppListPage>
  );
}
